import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Logo } from "@/components/brand/Logo";
import { cn } from "@/lib/utils";

const NAV_LINKS: { to: string; label: string }[] = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/about", label: "About" },
];

export function Nav() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close mobile panel when the route changes.
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  // Lock scroll when the mobile panel is open.
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-brand-midnight/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet rounded-sm"
          aria-label="VirtualDesk home"
        >
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => {
            const active = loc.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet rounded-sm px-1",
                  active ? "text-brand-ink" : "text-brand-ink/70 hover:text-brand-ink",
                )}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {/* Sign in goes to the wizard's StepLogin which handles three
              cases: in-flight onboarding (resume wizard), fully-onboarded
              user (redirected to <sub>.virtualdesk.ai/login), and unknown
              email (auth error). app.virtualdesk.ai is the platform-admin
              shell — never the customer signin destination. */}
          <Link
            to="/onboarding/login"
            className="text-sm text-brand-ink/80 hover:text-brand-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet rounded-sm px-1"
          >
            Sign in
          </Link>
          <Button asChild variant="primary" size="sm">
            <Link to="/register">Get started</Link>
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-ink hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-brand-midnight">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-base text-brand-ink/90 hover:text-brand-ink"
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <Button asChild variant="outline" size="md">
                <Link to="/onboarding/login">Sign in</Link>
              </Button>
              <Button asChild variant="primary" size="md">
                <Link to="/register">Get started</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
